import type { Plan, PlanStop } from '@trippath/shared';

import { arrival, arrivalAt as arriving, instantOf, later, wallOf } from './clock';

/** A way's times as a form has them, local at each end, and how they are kept as instants. */
export interface WayClock {
  /** The day it leaves, local where it leaves. */
  day: string;
  /** Its departure, `HH:MM` local where it leaves; none when not said. */
  departure: string | null;
  /** Its arrival, `HH:MM` local where it arrives; none when not said. */
  arrival: string | null;
  /** Its arrival, about, from a departure: local at its end, `11:35 +1`. */
  about: (departure: string) => string | null;
  /** The times typed, local at each end, as kept: instants in UTC, arriving the day it does. */
  kept: (times: { departure: string | null; arrival: string | null }) => {
    departureAt: string | null;
    arrivalAt: string | null;
  };
}

/**
 * The way on from `from`, to a place in zone `to`: its times local at each end — the day it
 * leaves its own, or the day the stop is left — and how times typed are kept. A change on the way
 * is left after it is reached, `came`: the clock it leaves at is the first such after it.
 */
export function wayClock(
  from: PlanStop,
  to: string | null,
  came: string | null = null,
): WayClock {
  const out = from.out;
  const end = to ?? from.zone;
  const minutes = out ? out.minutes : null;
  const base =
    came === null
      ? out?.departureAt
        ? wallOf(out.departureAt, from.zone).day
        : from.end
      : wallOf(came, from.zone).day;
  const leaving = (time: string) => {
    const at = instantOf(base, time, from.zone);
    return came !== null && at < came ? instantOf(later(base, 1), time, from.zone) : at;
  };
  const departure = out?.departureAt ? wallOf(out.departureAt, from.zone).time : null;
  return {
    // Without its clock, the day of the flight it is planned on.
    day:
      departure === null
        ? (out?.flight?.day ?? base)
        : wallOf(leaving(departure), from.zone).day,
    departure,
    arrival: out?.arrivalAt ? wallOf(out.arrivalAt, end).time : null,
    about: (time) =>
      minutes === null
        ? null
        : arrival(wallOf(leaving(time), from.zone).day, time, minutes, from.zone, to),
    kept: (times) => {
      const departureAt = times.departure === null ? null : leaving(times.departure);
      const arrivalAt =
        times.arrival === null
          ? null
          : departureAt === null
            ? instantOf(base, times.arrival, end)
            : arriving(departureAt, times.arrival, minutes, from.zone, to);
      return { departureAt, arrivalAt };
    },
  };
}

/** When a change on the way is reached: its way in arrives; any other stop is its own. */
export function cameInto(plan: Plan, stop: PlanStop): string | null {
  const before = plan.stops[plan.stops.indexOf(stop) - 1];
  return stop.transfer ? (before?.out?.arrivalAt ?? null) : null;
}

/** A stop's way on in a plan, to the next stop's zone. */
export function wayClockIn(plan: Plan, stop: PlanStop): WayClock {
  const next = plan.stops[plan.stops.indexOf(stop) + 1];
  return wayClock(stop, next ? next.zone : null, cameInto(plan, stop));
}
